/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { pageTypes } from '@/constants/page';
import {
  getCategoryResultsFromCache,
  prefetchCategoryResults,
} from '@/features/category/api/getCategoryResults';
import { prefetchProductDataTopNavMenuV2 } from '@/features/topNav/api/getProductDataTopNavMenuV2';
import {
  prefetchProductShelfResults,
  getProductShelfResultsFromCache,
  prefetchProductShelfResultsInfinite,
} from '@/features/category/api/getProductShelfResults';
import {
  getProductShelfACESResultsFromCache,
  prefetchProductShelfACESResults,
  prefetchProductShelfACESResultsInfinite,
} from '@/features/category/api/getProductShelfACESResults';
import { prefetchCategoryFAQ } from '@/features/category/api/getCategoryFAQ';
import { prefetchCategoryYears } from '@/features/category/api/getCategoryYears';
import { prefetchCMSCategoryData } from '@/features/category/api/getCMSCategoryData';
import { mapProductShelfData } from '@/features/category/api/mapProductShelfData';
import { getPageTypeFromCache, prefetchPageType } from '@/features/category/api/getPageType';
import { getShopByPageName } from '@/features/category/utils/getShopByPageName';
import { getGlobalConfigFromCache, getFeatureFlag } from '@/features/globalConfig';
import { getHeaderDataFromCache } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { getPageDependentPrefooterParams } from '@/features/prefooter/utils/getPageDependentPrefooterParams';
import { prefetchProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { getACESShopByType, getShopByType } from '@/features/shelf/utils/getShopByType';
import { prefetchTopReviews } from '@/features/shelf/api/getTopReviews';
import { prefetchYearsTable } from '@/features/shelf/api/getYearsTable';
import { getSkuIdsForPage } from '@/features/shelf/utils/getSkuIdsForPage';
import { prefetchPreFooterData } from '@/features/prefooter/api/getPreFooterData';
import type { AppState } from '@/stores/interface';
import { ProductListProvider } from '@/features/shelf/context/ProductListProvider';
import { ProductListViewProvider } from '@/features/shelf/context/ProductListViewProvider';
import { handleLoadMoreRedirect, isEmptyObject } from '@/utils/common';
import { configureSharedPageData } from '@/utils/configureSharedPageData.server';
import {
  getPathnameFromAsPath,
  parseUrl,
  sanitizeUrlWithParamList,
  stringifyUrl,
} from '@/utils/urlHelpers';
import React from 'react';
import { dehydrate } from '@tanstack/react-query';
import root from 'window-or-global';
import RoutingComponent from '../components/RoutingComponent';
import { countryCodes } from '@/constants/locale';
import { routePaths } from '../constants/routePaths';
import type { PageContextType } from '../next-types';
import Router from 'next/router';
import { FACETED_URL, FACETED_URL_V2 } from '@/utils/validatorRegex';
import logger from '@/utils/logger';
import { getDevice, getDeviceType } from '@/utils/getDeviceType.server';
import { prefetchDynamicLinkMappings } from '@/features/dlm/api/getDynamicLinkMappings';
import { cookieConstant } from '@/constants/cookies';
import environmentConfig from '@/config/environment';
import { acceptableShelfQueryParams } from '@/constants/queryParams';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { prefetchSeoMetadataInfoResults } from '@/features/common/api/getSeoMetadata';
import { getCookies } from '@/utils/cookie';
import { normalizeMakeName } from '@/utils/normalizeMakeName';
import { normalizeModelName } from '@/utils/normalizeModelName';
import { is24ProductViewEnabled } from '@/hooks/useIs24ProductViewEnabled';
import { getKiboDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { prefetchCitrusShelfData } from '@/features/citrus/api/getCitrusShelfData';
import { prefetchCitrusCategoryData } from '@/features/citrus/api/getCitrusCategorypageData';
import {
  getCitrusShelfCarouselDataFromCache,
  prefetchCitrusShelfCarouselData,
} from '@/features/citrus/api/getCitrusShelfCarouselData';
import {
  getPLPDataFromCache,
  prefetchContentStackProductListingPageData,
} from '@/features/productListingPage/api/getContentStackProductListingPageData';
import type { PageTypeData } from '@/types';
import { getConfigFlagsFromCache, prefetchConfigFlags } from '@/features/shelf/api/getConfigFlags';
import { type NextApiRequest, type NextApiResponse } from 'next';
import { getRecsPerPage } from '@/features/contentstack/utils/getRecsPerPage';
import { getCSPLPResultList } from '@/features/contentstack/utils/getCSPLPResultList';
import { prefetchProductFinder } from '@/features/contentstack/api/getGCPProductFinder';
import { getAcesFlag } from '@/features/ymme/utils/acesFlags';
import { getFirstFacetValue } from '@/features/common/utils/getFirstFacetValue';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { prefetchCategoryByModel } from '@/features/category/api/getCategoryModels';
import { addVehicleFromQueryParams } from '@/features/sharableLink/utils/addVehicleFromQueryParams';
import {
  getCatalogConfigsFromCache,
  prefetchCatalogConfigs,
} from '@/features/common/api/getCatalogConfig';
import { type CatalogConfigsOptions } from '@/features/common';
import type {
  ProductShelfACESResultsModel,
  ProductShelfResultsModel,
} from '@/api/types/browse-search-types';
import type { ProductShelfACESResponse, ProductShelfResponse } from '@/features/category';

// TODO: replace req.url with asPath when Next bug is fixed
// https://github.com/vercel/next.js/issues/46876

export const config = {
  unstable_JsPreload: false,
};

function RoutingPage() {
  return <RoutingComponent />;
}

const CATEGORY_PAGE_TYPES: unknown[] = [
  pageTypes.TopNavCategory,
  pageTypes.LeafCategory,
  pageTypes.CategoryGrouping,
];

RoutingPage.getInitialProps = async (context: PageContextType) => {
  const { store, query, res, req, asPath } = context;
  const { seoUrlPath: path } = query;
  if (!Array.isArray(path)) {
    throw new Error('Invalid seoUrl path');
  }

  if (path[0] === 'videos') {
    if (__IS_SERVER__) {
      return {
        redirect: {
          destination: routePaths.azVideos,
          permanent: false,
        },
      };
    } else {
      void Router.replace(routePaths.azVideos);
    }

    return {};
  }

  if (path[0] === 'company' || path[0] === 'locations') {
    if (__IS_SERVER__) {
      res?.writeHead(302, { Location: req?.url }).end();
    } else {
      root.location.href = asPath;
    }
    return {};
  }

  if (path[0] === 'signin') {
    if (__IS_SERVER__) {
      res?.writeHead(302, { Location: routePaths.signIn }).end();
    } else {
      window.location.replace(routePaths.signIn);
    }
    return {};
  }

  if (__IS_SERVER__ && req) {
    handleLoadMoreRedirect(req as NextApiRequest, res as NextApiResponse);
    const parsedUrl = req.url ? parseUrl(req.url) : null;
    const isRequestForAsset = req?.url && parsedUrl?.url.includes('.');
    const cleanedReqUrl = parsedUrl ? stringifyUrl(parsedUrl) : undefined;
    if (isRequestForAsset) {
      logger.info({
        message: 'Skipping asset request',
        meta: {
          url: req.url,
        },
      });
      res?.writeHead(404, 'Not Found').end();
      return {};
    }

    const { axios, queryClient, initialAppState } = await configureSharedPageData(context);
    const device = getDevice(context.req);
    const deviceType = getDeviceType(context.req);

    const globalConfig = getGlobalConfigFromCache(queryClient);

    const headerData = getHeaderDataFromCache(queryClient);
    let pageTypeData: PageTypeData | null | undefined = null;
    const enable24ProductViewFlagValue = globalConfig?.IS_24_PRODUCT_VIEW_ENABLED === 'true';
    const enable24ProductView = is24ProductViewEnabled({
      query,
      deviceType,
      flagValue: enable24ProductViewFlagValue,
    });
    const isLoadMoreEnabled = getKiboDecisionFlag(queryClient, 'loadMoreEnabled');
    const country = getCountryFromLocale(initialAppState.locale);

    const citrusEnabled = globalConfig?.IS_CITRUS_ENABLED === 'true';
    const citrusShelfSponsoredCardsEnabled =
      globalConfig?.IS_CITRUS_SHELF_SPONSORED_CARDS_ENABLED === 'true';
    const citrusShelfBannersEnabled = globalConfig?.IS_CITRUS_SHELF_BANNERS_ENABLED === 'true';
    const hasFilters =
      (req.url && FACETED_URL.test(req.url.split('?')[0])) ||
      (req.url && FACETED_URL_V2.test(req.url.split('?')[0])) ||
      !!query.filters ||
      !!query.facet;

    const catalogConfigAPIEnabled = globalConfig?.CATALOG_CONFIG_API_ENABLED === 'true';

    let categoryCMSPromise: Promise<void> | undefined;
    let seoMetadataPromise: Promise<void> | undefined;

    const { acesEnabled, acesTransitionPhase, acesNonVehicleEndpointsEnabled } =
      getAcesFlag(queryClient);

    try {
      if (req?.url && !parsedUrl?.url.includes('.')) {
        await prefetchPageType(queryClient, axios, {
          canonicalPath: req.url,
          country,
          gcpUrlEnabled: acesEnabled && acesNonVehicleEndpointsEnabled,
        });
        pageTypeData = getPageTypeFromCache(queryClient, {
          canonicalPath: req.url,
          country,
          gcpUrlEnabled: acesEnabled && acesNonVehicleEndpointsEnabled,
        });
        if (pageTypeData?.redirectLocation) {
          const { intcmp } = context.query;
          const { url, query: queryParams } = parseUrl(pageTypeData.redirectLocation);
          const sanitizedRedirectPath = sanitizeUrlWithParamList(
            {
              ...queryParams,
              intcmp,
            },
            acceptableShelfQueryParams,
            url
          );

          res?.writeHead(301, { Location: sanitizedRedirectPath }).end();
          return {
            initialAppState,
            dehydratedState: dehydrate(queryClient),
          };
        }
      }
    } catch {
      res?.writeHead(301, { Location: '/errorPage' }).end();
      return {};
    }

    const botEnabledFacetPath = pageTypeData?.botEnabledFacetPath ?? undefined;

    const taxonomyPath = pageTypeData?.taxonomyPath ?? '';

    const brandtaxonomyPath = pageTypeData?.botEnabledFacetPath
      ? `${taxonomyPath}${pageTypeData.botEnabledFacetPath}`
      : taxonomyPath;
    const vehicleQuestions = extractVehicleQuestions(
      headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
      true
    ) as string;
    let citrusCategoryPromise;
    let plpCMSData;

    try {
      if (pageTypeData?.pageType !== pageTypes.ProductShelf) {
        const citrusEnabled =
          globalConfig?.IS_CITRUS_ENABLED === 'true' &&
          getKiboDecisionFlag(queryClient, 'citrusCategoryEnabled');
        if (citrusEnabled && !!pageTypeData?.catalogRepositoryName) {
          citrusCategoryPromise = prefetchCitrusCategoryData(
            queryClient,
            context,
            pageTypeData.catalogRepositoryName,
            initialAppState.eCookieId
          );
        }

        const hasCatalogPath = Boolean(pageTypeData?.categoryRepositoryIdPath);
        const hasMakeModelYearPath = Boolean(pageTypeData?.makeModelYearPath);

        const shouldUseV2MenuData =
          acesNonVehicleEndpointsEnabled &&
          acesEnabled &&
          pageTypeData?.pageType === pageTypes.CategoryGrouping;

        const preferredVehicle = getPreferredVehicle(headerData);

        await Promise.all([
          // Use this conditional pattern
          ...(shouldUseV2MenuData && (hasCatalogPath || hasMakeModelYearPath)
            ? [
                // v2 of category results prefetch
                prefetchProductDataTopNavMenuV2(
                  queryClient,
                  {
                    country: getCountryFromLocale(initialAppState.locale),
                    year: Number(preferredVehicle?.year) || undefined,
                    makeId: Number(preferredVehicle?.makeId) || undefined,
                    modelId: Number(preferredVehicle?.modelId) || undefined,
                    vehicleTypeId: Number(preferredVehicle?.vehicleTypeId) || undefined,
                    preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
                    categoryIdPath: pageTypeData?.categoryRepositoryIdPath,
                    storeId: Number(headerData?.storeNumber) || undefined,
                    userSegments: headerData?.segments?.sort().join(',') || undefined,
                    makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
                    recordType: 'APP',
                    ...(vehicleQuestions && { vehicleQuestions }),
                  },
                  axios
                ),
              ]
            : [
                // Original v1 of category results prefetch
                prefetchCategoryResults(axios, queryClient, {
                  taxonomyPath: pageTypeData?.taxonomyPath,
                  canonicalPath: pageTypeData?.canonicalPath,
                  makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
                  vehicleId: getPreferredVehicle(headerData)?.catalogVehicleId,
                  locale: initialAppState.locale ?? 'en-US',
                  storeId: headerData?.storeNumber,
                  pageType: pageTypeData?.pageType,
                  preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
                  recsPerPage: deviceType === 'bot' || enable24ProductView ? '24' : undefined,
                }),
              ]),
          citrusCategoryPromise,
        ]);
      } else {
        await prefetchContentStackProductListingPageData({
          queryClient,
          taxonomyPath: brandtaxonomyPath,
          context,
          productListingPageType: 'Default',
          userSegments: headerData?.segments?.sort() ?? [],
        });

        plpCMSData = getPLPDataFromCache({
          queryClient,
          taxonomyPath: brandtaxonomyPath,
          productListingPageType: 'Default',
          userSegments: headerData?.segments?.sort() ?? [],
        });

        const csResultList = getCSPLPResultList(plpCMSData);

        const recordsPerPage = getRecsPerPage({
          reqUrl: req.url,
          is24ProductViewEnabled: enable24ProductView,
          deviceType,
          csResultList,
          isCSProductListingPageEnabled: true,
          isLoadMoreEnabled,
        });
        const parsedUrl = parseUrl(req.url || '');
        const preferredVehicle = getPreferredVehicle(headerData);
        const commonOptions = {
          rewardsId: headerData?.myAccountMap?.rewardsId ?? '',
          eCookieId: initialAppState.eCookieId,
          path: cleanedReqUrl,
          preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
          taxonomyPath: pageTypeData?.taxonomyPath,
          canonicalPath: pageTypeData?.canonicalPath,
          botEnabledFacetPath,
          makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
          vehicleId: getPreferredVehicle(headerData)?.catalogVehicleId,
          locale: initialAppState.locale ?? 'en-US',
          recsPerPage: recordsPerPage ? String(recordsPerPage) : undefined,
          storeId: headerData?.storeNumber,
          pageType: pageTypeData?.pageType,
          pageNumber: query.pageNumber ? Number(query.pageNumber) : 1,
          ...(acesEnabled &&
            acesNonVehicleEndpointsEnabled && {
              makeId: preferredVehicle?.makeId,
              modelId: preferredVehicle?.modelId,
              vehicleTypeId: preferredVehicle?.vehicleTypeId,
              year: !!preferredVehicle?.year ? Number(preferredVehicle?.year) : undefined,
            }),
          ...(parsedUrl.query.ignoreVehicleSpecificProductsCheck !== undefined && {
            ignoreVehicleSpecificProductsCheck:
              parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'true'
                ? true
                : parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'false'
                ? false
                : undefined,
          }),
          ...(vehicleQuestions && { vehicleQuestions }),
        };

        if (!isLoadMoreEnabled) {
          await Promise.all([
            acesEnabled && acesNonVehicleEndpointsEnabled
              ? prefetchProductShelfACESResults(axios, queryClient, {
                  ...commonOptions,
                  partGroupId: pageTypeData.catalogRepositoryId ?? '',
                })
              : prefetchProductShelfResults(axios, queryClient, { ...commonOptions }),
          ]);
        } else {
          await Promise.all([
            acesEnabled && acesNonVehicleEndpointsEnabled
              ? prefetchProductShelfACESResultsInfinite(axios, queryClient, {
                  ...commonOptions,
                  partGroupId: pageTypeData.catalogRepositoryId ?? '',
                })
              : prefetchProductShelfResultsInfinite(axios, queryClient, { ...commonOptions }),
          ]);
        }
      }
    } catch {
      res?.writeHead(301, { Location: '/errorPage' }).end();
      return {};
    }

    if (pageTypeData?.pageType === pageTypes.CategoryGrouping) {
      categoryCMSPromise = prefetchCMSCategoryData(
        queryClient,
        pageTypeData?.taxonomyPath ?? '',
        pageTypeData?.subPageType ?? '',
        context,
        headerData?.segments?.sort() ?? [],
        {
          make: pageTypeData.make ?? null,
          model: pageTypeData.model ?? null,
          year: pageTypeData.year ?? null,
        }
      );
    }

    const shopByPageName = getShopByPageName(pageTypeData);

    const categoryResultsData = getCategoryResultsFromCache(queryClient, store, {
      preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
      taxonomyPath: pageTypeData?.taxonomyPath,
      canonicalPath: pageTypeData?.canonicalPath,
      makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
      vehicleId: getPreferredVehicle(headerData)?.catalogVehicleId,
      locale: initialAppState.locale ?? 'en-US',
      recsPerPage: deviceType === 'bot' || enable24ProductView ? '24' : undefined,
      storeId: headerData?.storeNumber,
      pageType: pageTypeData?.pageType,
    });

    const commonOptions = {
      rewardsId: headerData?.myAccountMap?.rewardsId ?? '',
      eCookieId: initialAppState.eCookieId,
      path: cleanedReqUrl,
      preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
      taxonomyPath: pageTypeData?.taxonomyPath,
      canonicalPath: pageTypeData?.canonicalPath,
      botEnabledFacetPath: pageTypeData?.botEnabledFacetPath || undefined,
      makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
      vehicleId: getPreferredVehicle(headerData)?.catalogVehicleId,
      locale: initialAppState.locale ?? 'en-US',
      recsPerPage: deviceType === 'bot' || enable24ProductView ? '24' : undefined,
      storeId: headerData?.storeNumber,
      pageType: pageTypeData?.pageType,
      pageNumber: query.pageNumber ? Number(query.pageNumber) : 1,
    };

    const categoryShelfResultsData =
      acesEnabled && acesNonVehicleEndpointsEnabled
        ? getProductShelfACESResultsFromCache(queryClient, store, {
            ...commonOptions,
            partGroupId: pageTypeData?.catalogRepositoryId ?? '',
          })
        : getProductShelfResultsFromCache(queryClient, store, { ...commonOptions });

    const partsProSpecificShelfData =
      acesEnabled && acesNonVehicleEndpointsEnabled
        ? undefined
        : (categoryShelfResultsData as ProductShelfResponse);

    const acesSpecificShelfData =
      acesEnabled && acesNonVehicleEndpointsEnabled
        ? (categoryShelfResultsData as ProductShelfACESResponse)
        : undefined;

    const catalogVehicleId = getPreferredVehicle(headerData)?.catalogVehicleId ?? '0';

    const productId = categoryShelfResultsData?.productShelfResults?.skuRecords?.find(
      (skuRecord) => Number(skuRecord.originalSystemCode) === 0 && skuRecord.recordType === 'app'
    )?.originalPartTypeId;

    // TODO: consider using catalog-configs instead of iterating through the content
    const csProductFinder = plpCMSData?.shelfpagebody?.find(
      (block) => 'product_finder' in block && !!block.product_finder?.product_finder?.[0]
    );
    if (
      !!productId &&
      !!csProductFinder &&
      !!catalogVehicleId &&
      getKiboDecisionFlag(queryClient, 'gcpProductFinderEnabled')
    ) {
      const productFinderOptions = {
        country,
        vehicleId: catalogVehicleId,
        productId,
      };
      await Promise.all([prefetchProductFinder(axios, queryClient, productFinderOptions)]);
    }

    const mappedCategorydata =
      partsProSpecificShelfData && mapProductShelfData(partsProSpecificShelfData, pageTypeData);

    const redirectPath =
      partsProSpecificShelfData?.redirectLocation ||
      acesSpecificShelfData?.redirectUrl ||
      categoryResultsData?.redirectLocation ||
      undefined;

    if (pageTypeData?.pageType === undefined) {
      if (req.url?.includes('.json')) {
        return {
          dehydratedState: dehydrate(queryClient),
          initialAppState,
        };
      }
      if (!res?.headersSent) {
        res?.writeHead(301, { Location: '/errorPage' }).end();
      }
    } else if (redirectPath && !req.url?.startsWith('/_next/')) {
      const parsedUrl = parseUrl(redirectPath);

      let sanitizedPath = redirectPath;

      if (!isEmptyObject(parsedUrl?.query)) {
        sanitizedPath = parsedUrl.url;
      }

      const { appData } = store.getState();
      const isBot = appData.deviceType === 'bot';

      // Do not send full path with query params for bots
      // We explicitly append recsPerPage: 24 for bots when constructing the URL when making the query in prefetchCategory.
      // However, even though we are requesting 24 recsPerPage,
      // SEO does not want this query param to show up in the redirect url since bots are technically not be able to change recsPerPage.
      const path = isBot ? sanitizedPath : redirectPath;
      if (!res?.headersSent) {
        res?.writeHead(301, { Location: path }).end();
      }
    }

    let skuDetailsPromise: Promise<void> | undefined;
    let dynamicLinksPromise: Promise<void> | undefined;
    let categoryFAQPromise: Promise<void> | undefined;
    let yearsTablePromise: Promise<void> | undefined;
    let categoryYearsPromise: Promise<void> | undefined;
    let citrusShelfCarouselPromise: Promise<void> | undefined;
    let configFlagsPromise: Promise<void> | undefined;
    let topReviewsPromise: Promise<void> | undefined;

    if (!!mappedCategorydata?.productId && initialAppState.locale === countryCodes.us) {
      configFlagsPromise = prefetchConfigFlags(
        {
          partNumber: mappedCategorydata.productId,
        },
        queryClient,
        axios
      );
    }

    let skuIds: string[] | undefined;
    if (pageTypeData?.pageType === pageTypes.ProductShelf && req.url) {
      if (
        citrusEnabled &&
        citrusShelfSponsoredCardsEnabled &&
        !hasFilters &&
        pageTypeData.catalogRepositoryName &&
        mappedCategorydata?.productId &&
        deviceType !== 'bot'
      ) {
        await configFlagsPromise;

        const configFlagsData = getConfigFlagsFromCache(
          {
            partNumber: mappedCategorydata.productId,
          },
          queryClient
        );

        if (
          configFlagsData?.IS_SPNSRD_ADVRTSMT_FL === 'false' ||
          (configFlagsData?.IS_SPNSRD_ADVRTSMT_FL === 'true' &&
            !headerData?.vehicleMap?.catalogVehicleId)
        ) {
          citrusShelfCarouselPromise = prefetchCitrusShelfCarouselData(
            queryClient,
            context,
            {
              category: pageTypeData.catalogRepositoryName,
              vehicleId: catalogVehicleId,
            },
            initialAppState.eCookieId
          );
        }
      }

      skuDetailsPromise = (citrusShelfCarouselPromise ?? Promise.resolve()).then(() => {
        const citrusData = getCitrusShelfCarouselDataFromCache(
          {
            category: pageTypeData?.catalogRepositoryName ?? '',
            vehicleId: catalogVehicleId,
          },
          queryClient
        );
        skuIds = getSkuIdsForPage(mappedCategorydata, req.url ?? '', citrusData);

        return skuIds.length > 0
          ? prefetchProductSkuDetails(axios, queryClient, {
              skuIds: !isLoadMoreEnabled && enable24ProductView ? skuIds.slice(0, 12) : skuIds,
              storeNumber: headerData?.storeNumber ?? '',
              vehicleId: catalogVehicleId,
              isNewDealsApiEnabled:
                getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true',
            })
          : undefined;
      });
      if (
        pageTypeData.catalogRepositoryId &&
        device.type === 'bot' &&
        !shopByPageName &&
        getFeatureFlag(globalConfig, 'DLM_SHELF_ENABLED') === 'true' &&
        catalogVehicleId === '0'
      ) {
        dynamicLinksPromise = prefetchDynamicLinkMappings(
          {
            type: 'generic',
            country: getCountryFromLocale(initialAppState.locale),
            seoRepositoryId: pageTypeData.catalogRepositoryId,
          },
          queryClient,
          axios
        );
      }
      if (
        pageTypeData.catalogRepositoryId &&
        device.type === 'bot' &&
        !!pageTypeData?.make &&
        getFeatureFlag(globalConfig, 'DLM_YMM_SHELF_ENABLED') === 'true' &&
        catalogVehicleId === '0'
      ) {
        dynamicLinksPromise = prefetchDynamicLinkMappings(
          {
            type: 'vehicle',
            country: getCountryFromLocale(initialAppState.locale),
            seoRepositoryId: pageTypeData.catalogRepositoryId,
            vehicleOptions: {
              make: normalizeMakeName(pageTypeData?.make),
              model: pageTypeData?.model ? normalizeModelName(pageTypeData?.model) : undefined,
              year: pageTypeData?.year ?? undefined,
            },
          },
          queryClient,
          axios
        );
      }

      if (
        getFeatureFlag(globalConfig, 'YEARS_TABLE_ENABLED') === 'true' &&
        pageTypeData?.pageType === pageTypes.ProductShelf &&
        shopByPageName === 'ShopByModel' &&
        partsProSpecificShelfData?.productShelfResults?.productId &&
        partsProSpecificShelfData?.productShelfResults?.make &&
        partsProSpecificShelfData?.productShelfResults?.model &&
        device.type === 'bot'
      ) {
        const catalogConfigsOptions: CatalogConfigsOptions = {
          itemId: partsProSpecificShelfData.productShelfResults.productId,
          itemType: 'product',
          country,
        };

        if (catalogConfigAPIEnabled) {
          await prefetchCatalogConfigs(catalogConfigsOptions, queryClient, axios);
        }
        const catalogConfigsData = getCatalogConfigsFromCache(catalogConfigsOptions, queryClient);

        if (
          catalogConfigAPIEnabled
            ? catalogConfigsData?.configs?.yearsTableEnabled
            : partsProSpecificShelfData.productShelfResults?.yearsTableEnableFlag
        ) {
          yearsTablePromise = prefetchYearsTable(
            queryClient,
            {
              productId: partsProSpecificShelfData.productShelfResults.productId,
              make: partsProSpecificShelfData.productShelfResults.make,
              model: partsProSpecificShelfData.productShelfResults.model,
              hasAssociatedPartType:
                partsProSpecificShelfData.productShelfResults?.hasAssociatedPartType,
              customerType: 'B2C',
              country: getCountryFromLocale(initialAppState?.locale),
            },
            axios
          );
        }
      }
    }

    if (
      deviceType === 'bot' &&
      pageTypeData?.pageType === pageTypes.ProductShelf &&
      categoryShelfResultsData?.productShelfResults &&
      req.url &&
      pageTypeData?.canonicalPath
    ) {
      const shopByType = categoryShelfResultsData?.productShelfResults
        ? acesEnabled && acesNonVehicleEndpointsEnabled
          ? getACESShopByType(
              categoryShelfResultsData.productShelfResults as ProductShelfACESResultsModel
            )
          : getShopByType(categoryShelfResultsData.productShelfResults as ProductShelfResultsModel)
        : null;

      if (shopByType === 'ShopByModel' || shopByType === 'ShopByYear') {
        categoryYearsPromise = prefetchCategoryYears(
          {
            seoUrl: getPathnameFromAsPath(req.url),
            locale: initialAppState.locale,
            year: categoryShelfResultsData.productShelfResults.year,
            preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
            shouldCallACES:
              acesEnabled &&
              acesNonVehicleEndpointsEnabled &&
              globalConfig?.SHOP_BY_PAGE_APIS_ENABLED === 'true',
            acesOptions: {
              makeId: acesSpecificShelfData?.productShelfResults?.makeId,
              modelId: acesSpecificShelfData?.productShelfResults?.modelId,
              partGroupId: acesSpecificShelfData?.productShelfResults?.partGroupId,
            },
          },
          queryClient,
          axios
        );
      }
    }

    await Promise.all([
      skuDetailsPromise,
      yearsTablePromise,
      prefetchPreFooterData(
        queryClient,
        {
          locale: initialAppState.locale,
          seourl: getPathnameFromAsPath(req.url ?? '').replace(/(\/bn\/|\/b\/).*/, ''),
          vehicleId: catalogVehicleId === '' ? '0' : catalogVehicleId,
          categoryIdPath: pageTypeData?.catalogIdPath,
          repositoryId: pageTypeData?.catalogId,
          vehicleQuestions: headerData?.vehicleMap?.vehicleQuestionsIds,
          year: headerData?.vehicleMap?.year,
          makeId: headerData?.vehicleMap?.makeId?.toString(),
          modelId: headerData?.vehicleMap?.modelId?.toString(),
          vehicleTypeId: headerData?.vehicleMap?.vehicleTypeId?.toString(),
          shouldCallACES: acesEnabled,
          ...getPageDependentPrefooterParams({
            isPDP: false,
            pageTypeData: pageTypeData ?? undefined,
            productDetails: undefined,
          }),
        },
        axios
      ),
      citrusEnabled &&
      citrusShelfBannersEnabled &&
      pageTypeData?.pageType === pageTypes.ProductShelf &&
      !!pageTypeData?.catalogRepositoryName
        ? prefetchCitrusShelfData(
            queryClient,
            context,
            pageTypeData.catalogRepositoryName,
            initialAppState.eCookieId
          )
        : undefined,
      configFlagsPromise,
      categoryYearsPromise,
    ]);

    if (query.vehicleId || query.year) {
      await addVehicleFromQueryParams(
        queryClient,
        query,
        initialAppState.locale,
        axios,
        asPath,
        { acesEnabled, acesTransitionPhase },
        {
          pageName: 'SEOUrlPath',
        }
      );
    }

    if (
      globalConfig &&
      pageTypeData &&
      (shopByPageName == 'ShopByModel' || shopByPageName == 'ShopByYear') &&
      initialAppState?.locale !== countryCodes.mx &&
      deviceType === 'bot' &&
      mappedCategorydata?.productId
    ) {
      topReviewsPromise = prefetchTopReviews(
        axios,
        queryClient,
        globalConfig,
        {
          minimumReviewRating: undefined,
          partTypeId: mappedCategorydata.productId,
          pageType: shopByPageName,
          vehicleMake: pageTypeData.make ?? undefined,
          vehicleModel: pageTypeData.model ?? undefined,
          vehicleYear: pageTypeData.year ?? undefined,
        },
        initialAppState?.locale ?? 'en-US'
      );
    }

    if (
      queryClient &&
      req.url &&
      Boolean(pageTypeData?.make) &&
      !Boolean(pageTypeData?.model) &&
      pageTypeData?.pageType === pageTypes.CategoryGrouping
    ) {
      await prefetchCategoryByModel(
        {
          preview: false,
          seoUrl: req.url,
          locale: initialAppState.locale ?? 'en-US',
        },
        queryClient,
        axios
      );
    }

    if (queryClient && CATEGORY_PAGE_TYPES.includes(pageTypeData?.pageType)) {
      if (deviceType === 'bot' && initialAppState?.locale === countryCodes.us) {
        if (pageTypeData?.catalogRepositoryId) {
          if (
            !shopByPageName &&
            getFeatureFlag(globalConfig, 'DLM_CATEGORY_ENABLED') === 'true' &&
            catalogVehicleId === '0'
          ) {
            dynamicLinksPromise = prefetchDynamicLinkMappings(
              {
                type: 'generic',
                country: getCountryFromLocale(initialAppState.locale),
                seoRepositoryId: pageTypeData.catalogRepositoryId,
              },
              queryClient,
              axios
            );
          }
          if (
            pageTypeData.makeModelYearPath &&
            pageTypeData.make &&
            getFeatureFlag(globalConfig, 'DLM_YMM_CATEGORY_ENABLED') === 'true' &&
            catalogVehicleId === '0'
          ) {
            dynamicLinksPromise = prefetchDynamicLinkMappings(
              {
                type: 'vehicle',
                country: getCountryFromLocale(initialAppState.locale),
                seoRepositoryId: pageTypeData.catalogRepositoryId,
                vehicleOptions: {
                  make: normalizeMakeName(pageTypeData?.make),
                  model: pageTypeData.model ? normalizeModelName(pageTypeData.model) : undefined,
                  year: pageTypeData.year ?? undefined,
                },
              },
              queryClient,
              axios
            );
          }

          categoryFAQPromise = prefetchCategoryFAQ(
            axios,
            initialAppState.locale,
            queryClient,
            {
              seoRepositoryType: 'CATEGORY',
              categoryId: pageTypeData.catalogRepositoryId,
            },
            globalConfig
          );
        }
      }
    }

    if (
      (pageTypeData?.pageType === pageTypes.ProductShelf ||
        CATEGORY_PAGE_TYPES.includes(pageTypeData?.pageType)) &&
      !!pageTypeData?.canonicalPath
    ) {
      seoMetadataPromise = prefetchSeoMetadataInfoResults(axios, queryClient, {
        vehicleMake: pageTypeData?.make ?? undefined,
        vehicleModel: pageTypeData?.model ?? undefined,
        vehicleYear: pageTypeData?.year ?? undefined,
        seoUrl: pageTypeData?.canonicalPath,
        botEnabledFacetPath,
        country: getCountryFromLocale(initialAppState?.locale),
        customerType: 'B2C',
        preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
        pageType: pageTypeData?.subPageType,
        firstFacetValue: getFirstFacetValue(
          categoryShelfResultsData?.productShelfResults?.facets,
          botEnabledFacetPath
        ),
        shouldCallACES: acesEnabled && acesNonVehicleEndpointsEnabled,
      });
    }

    await Promise.all([
      categoryCMSPromise,
      seoMetadataPromise,
      categoryFAQPromise,
      dynamicLinksPromise,
      topReviewsPromise,
    ]);

    let isListView =
      globalConfig?.IS_DEFAULT_GRID_VIEW_ENABLED === 'true'
        ? false
        : initialAppState?.locale !== countryCodes.mx;
    const cookies = getCookies({ req, res });
    const shelfListGridViewCookie = cookies[cookieConstant.names.shelfListGridView];

    let filterOpenStates = '{}';
    const filterOpenStatesCookie = cookies[cookieConstant.names.filterOpenStates];

    if (shelfListGridViewCookie) {
      isListView = shelfListGridViewCookie !== cookieConstant.values.shelfListGridView;
    }

    if (filterOpenStatesCookie) {
      filterOpenStates = filterOpenStatesCookie;
    }

    return {
      dehydratedState: dehydrate(queryClient),
      initialAppState,
      isListView,
      filterOpenStates,
    };
  }

  return {};
};

RoutingPage.getLayout = (
  page: React.ReactElement,
  pageProps: { isListView?: boolean; filterOpenStates?: string; initialAppState: AppState }
) => {
  let isListView = true;

  if (
    'initialAppState' in pageProps &&
    'IS_DEFAULT_GRID_VIEW_ENABLED' in pageProps.initialAppState
  ) {
    isListView = !pageProps.initialAppState.IS_DEFAULT_GRID_VIEW_ENABLED;
  }

  if ('isListView' in pageProps && pageProps.isListView != null) {
    isListView = pageProps.isListView;
  }

  let filterOpenStates: string | undefined = '{}';
  if ('filterOpenStates' in pageProps) {
    filterOpenStates = pageProps.filterOpenStates;
  }

  if ('initialAppState' in pageProps && pageProps.initialAppState.locale === 'es-MX') {
    isListView = pageProps.initialAppState.locale !== 'es-MX';
  }

  if ('initialAppState' in pageProps && pageProps.initialAppState.locale === countryCodes.ptBr) {
    isListView = false;
  }

  return (
    <ProductListProvider>
      <ProductListViewProvider isListView={isListView} filterOpenStates={filterOpenStates}>
        {page}
      </ProductListViewProvider>
    </ProductListProvider>
  );
};

export default RoutingPage;
